<i18n lang="yaml">
en:
  agentList: "All agents"
  tabs:
    profile: "Profile"
    listing: "Listing"
    team: "Other agents"
  profilePage: "This is profile preview"
  visitWebSite: "Visit website"
  showOnMap: "Show on map"
  intro: ""
  pricing: ""
  makePublic: "Make profile public"
  cover:
    upload: "Upload cover"
    delete: "Delete cover"
  avatar:
    upload: "Upload"
    delete: "Delete"
ja:
  agentList: "エージェント一覧"
  title: "{company} {name}"
  tabs:
    profile: "プロフィール"
    listing: "取扱物件"
    team: "他スタッフ"
  profilePage: "プロフィールページ"
  visitWebSite: "Webサイトはこちら"
  showOnMap: "アクセスはこちら"
  intro: |
    プロフィールページを使って、不動産プロフェッショナルとしてのあなたの魅力を発信しましょう。
    プロフィール情報を充実させることで、顧客からの信頼を獲得し、反響へと繋げることができます。{br}
    情報が入力できたら、スイッチをONにしてページを公開しましょう！
  pricing: "* 本サービスは無料です。情報を公開することで料金が発生することはありません。"
  makePublic: "プロフィールページを公開する"
  cover:
    upload: "画像をアップロード"
    reupload: "画像を変更"
    delete: "画像を削除"
  avatar:
    upload: "変更"
    delete: "削除"
</i18n>
<script setup lang="ts">
import { watch, computed, onBeforeMount, onServerPrefetch } from "vue";
import { useRoute, RouterLink, RouterView } from "vue-router";
import { storeToRefs } from "pinia";

import sentry from "@/modules/sentry";

import { useContext } from "vite-ssr";
import { useI18n } from "vue-i18n";
import { useHead } from "@unhead/vue";
import { use as useSession } from "@/stores/session";
import { useAPI } from "@/composables/api";

import LayoutHeader from "@/components/LayoutHeader.vue";
import LayoutBreadcrumb from "@/components/LayoutBreadcrumb.vue";
import LayoutFooter from "@/components/LayoutFooter.vue";
import UIUpload from "@/components/ui/Upload.vue";
import UIToggle from "@/components/ui/Toggle.vue";

// inject
const route = useRoute();
const session = useSession();
const api = useAPI();
const {
  active,
  agent,
  agentAvatar,
  company,
  companyLogo,
  companyLogoLarge,
  mapUrl,
  address,
  publicPage,
} = storeToRefs(session);

const { t } = useI18n();

const context = useContext();

async function load() {
  const { id, organizationId } = route.params;

  await Promise.allSettled([
    api.call("agent", id, organizationId).then((res) => {
      const { agent, company, team } = res.data;
      session.agent = agent;
      session.organization = company;
      session.members = team.profiles;
    }),
  ]);
}

watch(
  () => route.params.id,
  async () => {
    checkSession();
    await load();
  }
);

onServerPrefetch(async () => {
  try {
    await load();
    Object.assign(context.initialState, { session: session.$state });
  } catch (err) {
    sentry.captureException(err);
    throw err;
  }
});

function checkSession() {
  const re = /(^|;)\s*openroom_forest_exp\s*=\s*([^;]+)/;
  const match = document.cookie.match(re);
  if (match?.length) {
    const flag = match.pop() as string,
      [, organizationId] = flag.split("_");
    if (organizationId == route.params.organizationId) {
      api
        .call("refresh", organizationId)
        .then(() => api.call("me"))
        .then((res) => {
          session.active = res.data.login.id.toString() == route.params.id;
        })
        .catch((err) => {
          if (err.response?.status == 401) session.active = false;
        });
    }
  }
}

onBeforeMount(async () => {
  checkSession();
  Object.assign(session, context.initialState.session);
  if (!session.agent) await load();
  // console.log(document.cookie)
});

const coverStyle = computed(() => {
  const file = session.agent?.openroom.cover;
  if (file?.url) {
    return {
      "--url": `url(${file.url})`,
    };
  }
  return null;
});

interface Nested {
  [key: string]: Nested | string | string[] | null;
}
async function updateProfile(update: Record<string, string | string[] | null>) {
  await api.call("updateProfile", update);
  for (const [path, value] of Object.entries(update)) {
    const keys = path.split("."),
      key = keys.pop() as string;
    const obj = keys.reduce(
      (obj, key) => obj?.[key] as Nested,
      session.agent as Nested
    ) as Record<string, string | string[] | null>;
    if (obj && key) obj[key] = value;
  }
}

async function uploadAndUpdateProfile(path: string, [file]: FileList) {
  const { data } = await api.call("upload", "profile-pictures", file);
  await updateProfile({ [path]: data });
}

async function onPageStatus(value: boolean) {
  const status: "public" | "private" = value ? "public" : "private";
  await updateProfile({ "openroom.status": status });
}

function toKatakana(s: string) {
  return s.replace(/[\u3041-\u3096]/g, (match) => {
    const chr = match.charCodeAt(0) + 0x60;
    return String.fromCharCode(chr);
  });
}

const visible = computed(() => publicPage.value || active.value);
const breadcrumbs = computed(() => {
  const title = agent.value?.personal.fullname ?? "";
  return [
    {
      name: "Agents",
      title: t("agentList"),
    },
    {
      title,
      name: title,
      path: route.path,
    },
  ];
});

useHead({
  title: () =>
    t("title", {
      name: agent.value?.personal.fullname ?? "",
      company: company.value?.name ?? "",
    }),
});
</script>

<template>
  <LayoutHeader />
  <LayoutBreadcrumb :breadcrumbs="breadcrumbs" />
  <div class="not-found" v-if="!visible" />
  <template v-else>
    <div class="intro cl-white t16" v-if="active">
      <div class="frame">
        <div class="left">
          <h2 v-t="'profilePage'" />
          <i18n-t tag="p" keypath="intro"
            ><template #br><br /></template
          ></i18n-t>
          <div class="make-public u-row">
            <span v-t="'makePublic'" />
            <UIToggle :value="publicPage" @update:value="onPageStatus" />
          </div>
          <small v-t="'pricing'" />
        </div>
      </div>
    </div>
    <div
      class="cover cl-white u-col"
      v-if="agent"
      :class="{ empty: !coverStyle }"
    >
      <div class="overlay" :style="coverStyle"></div>
      <div class="edit u-row" v-if="active">
        <UIUpload
          @files="uploadAndUpdateProfile('openroom.cover', $event)"
          v-slot="{ upload }"
        >
          <button
            type="button"
            class="btn primary w6"
            :class="{ outline: !coverStyle }"
            @click="upload"
          >
            <FontAwesomeIcon class="icon" :icon="['far', 'image']" />
            <span v-t="coverStyle ? 'cover.reupload' : 'cover.upload'" />
          </button>
        </UIUpload>
        <button
          type="button"
          class="btn primary outline w6"
          @click="
            updateProfile({
              'openroom.cover.url': null,
              'openroom.cover.key': null,
            })
          "
          v-if="coverStyle"
        >
          <FontAwesomeIcon class="icon" :icon="['far', 'trash-can']" />
          <span v-t="'cover.delete'" />
        </button>
      </div>
      <div
        v-if="agentAvatar"
        class="avatar u-col"
        :class="{ active }"
        :style="{ '--url': 'url(' + agentAvatar + ')' }"
        :alt="agent.personal?.fullname"
      >
        <div class="edit u-col" v-if="active">
          <UIUpload
            @files="uploadAndUpdateProfile('avatar', $event)"
            v-slot="{ upload }"
          >
            <button
              type="button"
              class="btn primary w6"
              @click="upload"
              v-t="'avatar.upload'"
            />
          </UIUpload>
          <button
            type="button"
            class="btn white w6"
            @click="updateProfile({ 'avatar.key': null, 'avatar.url': null })"
            v-t="'avatar.delete'"
          />
        </div>
      </div>
      <span class="name t20">{{ agent.personal?.fullname }}</span>
      <span
        class="furigana t16"
        v-if="
          agent.personal?.family_name_furigana &&
          agent.personal?.given_name_furigana
        "
      >
        {{ toKatakana(agent.personal.family_name_furigana) }}
        {{ toKatakana(agent.personal.given_name_furigana) }}
      </span>
    </div>
    <div class="tabs u-row t16 t-center">
      <RouterLink
        class="tab u-col"
        :class="{ active: route.name == 'agentHome' }"
        :to="{ name: 'agentHome' }"
        replace
      >
        <div class="u-row">
          <FontAwesomeIcon class="icon" :icon="['fas', 'user']" />
          <span v-t="'tabs.profile'" />
        </div>
      </RouterLink>
      <RouterLink
        class="tab u-col"
        :class="{ active: route.name == 'agentList' }"
        :to="{ name: 'agentList' }"
        replace
      >
        <div class="u-row">
          <FontAwesomeIcon class="icon" :icon="['fas', 'house']" />
          <span v-t="'tabs.listing'" />
        </div>
      </RouterLink>
      <RouterLink
        class="tab u-col"
        :class="{ active: route.name == 'team' }"
        :to="{ name: 'team' }"
        replace
      >
        <div class="u-row">
          <FontAwesomeIcon class="icon" :icon="['fas', 'users']" />
          <span v-t="'tabs.team'" />
        </div>
      </RouterLink>
    </div>
    <main class="frame">
      <section class="panes u-row">
        <div />
        <RouterView />
        <section class="side">
          <div class="company" v-if="company">
            <img
              v-if="companyLogoLarge || companyLogo"
              :src="companyLogoLarge ?? companyLogo"
              :alt="company.name"
            />
            <!--
            <p class="licence" v-if="company.brokeragePrefecture">
              {{ company.brokeragePrefecture }}({{
                company.brokerageLicenceNumber
              }})第{{ company.brokerageLicenceNumber2 }}号
            </p>
            -->
            <div class="row u-row">
              <div class="icon">
                <FontAwesomeIcon class="icon" :icon="['fas', 'building']" />
              </div>
              <p>{{ company.name }}</p>
            </div>
            <div class="row u-row">
              <div class="icon">
                <FontAwesomeIcon
                  class="icon"
                  :icon="['fas', 'map-marker-alt']"
                />
              </div>
              <p class="address" v-if="company.postalcode">
                <!-- 〒{{ company.postalcode }} -->
                {{ address }}
              </p>
            </div>
            <div class="actions">
              <a
                class="btn outline primary w6"
                v-if="company.website"
                :href="company.website"
                target="_blank"
              >
                <FontAwesomeIcon class="icon" :icon="['fas', 'globe']" />
                <span v-t="'visitWebSite'" />
              </a>
              <a
                class="btn outline primary w6"
                v-if="mapUrl"
                :href="mapUrl"
                target="_blank"
              >
                <FontAwesomeIcon
                  class="icon"
                  :icon="['fas', 'map-marker-alt']"
                />
                <span v-t="'showOnMap'" />
              </a>
              <a
                class="btn outline primary w6"
                v-if="company.phone"
                :href="'tel:' + company.phone"
              >
                <FontAwesomeIcon class="icon" :icon="['fas', 'phone']" />
                <span>{{ company.phone }}</span>
              </a>
            </div>
          </div>
        </section>
      </section>
    </main>
  </template>
  <LayoutFooter />
</template>

<style scoped>
.cover,
.tabs {
  width: 100%;
}
.cover {
  height: 320px;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
}
@media (max-width: 768px) {
  .cover {
    height: 230px;
  }
}
.cover .overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-image: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.3) 0%,
      rgba(0, 0, 0, 0.3) 100%
    ),
    var(--url);
}
.cover.empty .overlay {
  background: linear-gradient(108deg, #f2f8fc -4.36%, #fbf1e2 101.74%);
}
.cover > * {
  z-index: 1;
}
.cover > .edit .btn {
  padding: 0 16px;
}
.cover > .edit {
  position: absolute;
  right: 32px;
  top: 24px;
}
.cover > .edit .btn {
  height: 40px;
  font-weight: 600;
}
.cover > .edit .btn + .btn {
  margin-left: 10px;
}
.cover .avatar {
  width: 160px;
  height: 160px;
  border: solid 1px var(--cl-white);
  border-radius: 50%;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-color: var(--cl-white);
  background-image: var(--url);
  justify-content: center;
}
@media (max-width: 768px) {
  .cover .avatar {
    width: 120px;
    height: 120px;
  }
}
.cover .avatar .edit {
  display: none;
}
.cover .avatar.active:hover {
  background-image: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.3) 0%,
      rgba(0, 0, 0, 0.3) 100%
    ),
    var(--url);
}
.cover .avatar.active:hover .edit {
  display: flex;
}
.cover .avatar .edit .btn {
  padding: 0 16px;
  height: 32px;
  font-weight: 600;
}
.cover .avatar .edit .btn + .btn {
  margin-top: 8px;
}
.cover .edit .btn.white {
  border: solid 1px var(--cl-white);
  color: var(--cl-white);
}
.cover .edit .btn.white:hover {
  background-color: var(--cl-white);
  color: transparent;
  color: rgba(0, 0, 0, 0.2);
}
.cover .name {
  margin-top: 12px;
}
.cover .name,
.cover .furigana {
  font-weight: 600;
  /*
  -webkit-text-stroke: 0.5px var(--cl-black-60);
  text-stroke: 0.5px var(--cl-black-60);
   */
}
.cover .furigana {
  margin-top: 4px;
}
.cover.empty .name {
  color: var(--cl-black);
}
.cover.empty .furigana {
  color: var(--cl-black-40);
}
.tabs {
  justify-content: center;
  border-width: 1px 0 1px 0;
  border-color: var(--cl-black-20);
  border-style: solid;
  flex-shrink: 0;
}
.tabs .tab {
  color: var(--cl-black-40);
  height: 56px;
  width: 148px;
  font-size: 15px;
  justify-content: center;
  position: relative;
}
.tabs .tab.active {
  color: var(--cl-blue);
}
.tabs .tab.active:after {
  content: "";
  width: 100%;
  height: 3px;
  position: absolute;
  top: 100%;
  background-color: var(--cl-blue);
}
.tabs .tab svg {
  width: 24px;
}
.tabs .tab span {
  margin-left: 6px;
}
@media (max-width: 768px) {
  .tabs .tab > .u-row {
    flex-direction: column;
  }
  .tabs .tab span {
    margin-top: 4px;
    margin-left: 0;
    font-size: 12px;
  }
}
main {
  padding: 24px 0;
}
main .panes {
  align-items: start;
  justify-content: space-between;
}
@media (max-width: 768px) {
  main {
    margin: 0;
    max-width: 100%;
    padding-bottom: 0;
  }
  main .panes {
    flex-direction: column;
  }
}
.side {
  margin-left: 35px;
}
.side .company {
  border: solid 1px var(--cl-black-05);
  border-radius: 8px;
  padding: 16px;
  width: 358px;
}
@media (max-width: 768px) {
  .side {
    width: 100%;
    background: linear-gradient(108deg, #f2f8fc -4.36%, #fbf1e2 101.74%);
    margin: 32px 0 0 0;
  }
  .side .company {
    background-color: var(--cl-white);
    margin: 24px auto;
    width: calc(100% - 32px);
  }
}
.side .company img {
  max-height: 100px;
  max-width: 300px;
  margin: 0 auto;
  border-radius: 2px;
  margin-bottom: 32px;
}
@media (max-width: 768px) {
  .side .company img {
    max-width: 90%;
  }
}
.side .company .actions,
.side .company .name {
  margin-top: 15px;
}
.side .company .row {
  color: var(--cl-black-60);
  margin-top: 8px;
}
.side .company .row .icon {
  width: 20px;
  height: 20px;
  margin-right: 8px;
}
.side .company .row .icon svg {
  display: block;
  margin: auto;
  width: 14px;
  height: 18px;
}
.side .company .actions a {
  margin-top: 16px;
  height: 40px;
}
.side .company .actions a svg {
  fill: var(--cl-orange);
}
.side .company .actions a:hover svg {
  fill: var(--cl-white);
}
.intro {
  width: 100%;
  background-color: var(--cl-black-90);
  padding: 32px 0;
}
.intro .left {
  max-width: 800px;
}
.intro .left p,
.intro .left small {
  margin-top: 8px;
}
.intro .make-public {
  margin-top: 32px;
}
.intro .make-public .ui-toggle {
  margin-left: 8px;
}
</style>
<style>
body {
  overflow-y: auto;
}
</style>
